import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CustomAlertService } from './custom-alert.service';
import { NormalizedError } from '../interceptors/error-normalizer.interceptor';

@Injectable({ providedIn: 'root' })
export class ErrorService {
  constructor(private alert: CustomAlertService, private router: Router) {}

  async handle(err: NormalizedError | any) {
    const e: NormalizedError = err?.status ? err : { status: 0, message: String(err), raw: err };

    // Common handling by status
    if (e.status === 0) {
      // network error
      await this.alert.showError('Problema de conexión', 'Verifica tu conexión a internet e inténtalo de nuevo.');
      return;
    }

    if (e.status === 401) {
      await this.alert.showWarning('Sesión expirada', 'Tu sesión ha expirado. Serás redirigido al inicio.');
      // Carga completa del documento (no router.navigate): tira el estado en
      // memoria de los servicios del usuario anterior. Mismo criterio que
      // ErrorInterceptor, que normalmente ya salió antes de llegar aquí.
      localStorage.clear();
      window.location.assign('/login');
      return;
    }

    if (e.status === 403) {
      await this.alert.showError('Acceso denegado', e.message || 'No tienes permisos para realizar esta acción.');
      return;
    }

    if (e.status === 404) {
      await this.alert.showError('No encontrado', e.message || 'El recurso solicitado no fue encontrado.');
      return;
    }

    if (e.status === 422) {
      if (e.details) {
        // validation errors - return details for component to handle
        return Promise.reject(e);
      } else if (e.message) {
        // Show the specific backend message for 422 errors
        await this.alert.showError('Error de validación', e.message);
        return;
      }
    }

    // Handle 400 Bad Request errors
    if (e.status === 400) {
      const message = e.message || 'La solicitud contiene datos inválidos.';
      await this.alert.showError('Solicitud incorrecta', message);
      return;
    }

    // Handle 500+ server errors
    if (e.status >= 500) {
      // Use the message from the interceptor if available (it's already user-friendly)
      const message = e.message || 'No pudimos procesar tu solicitud';
      await this.alert.showError('Error interno del servidor', message);
      return;
    }

    // fallback: show message if available for any other status code
    const title = this.getErrorTitle(e.status);
    const text = e.message || 'Se ha producido un error inesperado. Intenta de nuevo más tarde.';
    await this.alert.showError(title, text);
    return;
  }

  private getErrorTitle(status: number): string {
    if (status >= 500) return 'Error interno';
    if (status >= 400) return 'Problema detectado';
    return 'Error';
  }
}
